import { type ReactNode, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
} from 'lucide-react';
import {
  MEETING_TYPES,
  type IngestStatus,
  type MeetingNote,
  type MeetingNoteOwner,
} from '../../lib/meetingNotesApi';
import {
  useDeleteMeetingNote,
  useMeetingNotes,
  useReIngestMeetingNote,
} from '../../hooks/useMeetingNotes';
import { formatDate } from '../../lib/format';
import { IngestStatusBadge } from './IngestStatusBadge';
import { MeetingNoteForms } from './MeetingNoteForms';
import { MeetingNoteDetail } from './MeetingNoteDetail';
import { Select } from '../ui/Select';
import { DateField } from '../ui/DateField';
import { useConfirm } from '../ui/ConfirmProvider';

type Mode = 'list' | 'manual' | 'ingest';

export interface MeetingNotesTabProps {
  owner: MeetingNoteOwner;
  /**
   * Owning project id when notes are project-scoped. Used to make action
   * items clickable to their created Tasks. For project notes this equals
   * owner.projectId; lead/client notes leave it undefined.
   */
  projectId?: string;
  /** Compact spacing/typography for inline use (e.g. the Lead section). */
  variant?: 'tab' | 'inline';
  /** Hide every write control (create, process, edit, delete, re-ingest). */
  readOnly?: boolean;
  /** Extra buttons shown beside „Procesează cu AI” (e.g. „Generează minuta” on a meeting). */
  extraActions?: ReactNode;
}

function meetingTypeLabel(t: string): string {
  return t.charAt(0) + t.slice(1).toLowerCase();
}

export function MeetingNotesTab({
  owner,
  projectId,
  variant = 'tab',
  readOnly = false,
  extraActions,
}: MeetingNotesTabProps) {
  const { data: notes = [], isLoading, isError } = useMeetingNotes(owner);
  const [mode, setMode] = useState<Mode>('list');
  const [openId, setOpenId] = useState<string | null>(null);

  // Filters
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const deleteMutation = useDeleteMeetingNote(owner);
  const reIngestMutation = useReIngestMeetingNote(owner);
  const confirm = useConfirm();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return notes
      .filter((n) => (typeFilter ? n.meetingType === typeFilter : true))
      .filter((n) => (statusFilter ? n.ingestStatus === (statusFilter as IngestStatus) : true))
      .filter((n) => (from ? new Date(n.date) >= new Date(from) : true))
      .filter((n) => (to ? new Date(n.date) <= new Date(`${to}T23:59:59`) : true))
      .filter((n) =>
        q
          ? `${n.title} ${n.summary ?? ''} ${n.attendees ?? ''} ${n.content}`
              .toLowerCase()
              .includes(q)
          : true,
      );
  }, [notes, typeFilter, statusFilter, search, from, to]);

  const inline = variant === 'inline';

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className={inline ? 'text-sm font-semibold text-gray-800' : 'sr-only'}>
          Notițe întâlnire
        </h3>
        {!readOnly && (
          // Phones: the two actions as equal halves of a full-width row.
          <div className="grid w-full grid-cols-2 gap-2 sm:ml-auto sm:flex sm:w-auto sm:flex-wrap sm:items-center">
            {extraActions}
            <button
              type="button"
              onClick={() => setMode(mode === 'ingest' ? 'list' : 'ingest')}
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Sparkles size={15} /> Procesează cu AI
            </button>
            <button
              type="button"
              onClick={() => setMode(mode === 'manual' ? 'list' : 'manual')}
              className="inline-flex items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              <Plus size={15} /> Notiță nouă
            </button>
          </div>
        )}
      </div>

      {mode === 'manual' && (
        <MeetingNoteForms owner={owner} kind="manual" onDone={() => setMode('list')} />
      )}
      {mode === 'ingest' && (
        <MeetingNoteForms owner={owner} kind="ingest" onDone={() => setMode('list')} />
      )}

      {/* Filters. Phones: search across the top, the four filters in two
          equal columns below (they wrapped into a ragged mix of widths). */}
      {notes.length > 0 && (
        <div className="mb-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
          <input
            placeholder="Caută notițe…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ui-input col-span-2 sm:w-auto sm:min-w-[160px] sm:flex-1"
          />
          <Select
            label="Tip întâlnire"
            hideLabel
            size="sm"
            clearable
            value={typeFilter}
            onChange={setTypeFilter}
            placeholder="Toate tipurile"
            options={MEETING_TYPES.map((t) => ({ value: t, label: meetingTypeLabel(t) }))}
            className="sm:w-40"
          />
          <Select
            label="Status procesare"
            hideLabel
            size="sm"
            clearable
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Orice status"
            options={[
              { value: 'PENDING', label: 'În așteptare' },
              { value: 'PROCESSING', label: 'Se procesează' },
              { value: 'COMPLETED', label: 'Indexat' },
              { value: 'FAILED', label: 'Eșuat' },
            ]}
            className="sm:w-40"
          />
          <DateField value={from} onChange={setFrom} placeholder="De la data" className="sm:w-40" />
          <DateField value={to} onChange={setTo} placeholder="Până la data" className="sm:w-40" />
        </div>
      )}

      {isLoading ? (
        <p className="py-8 text-center text-sm text-gray-500">Se încarcă notițele de întâlnire…</p>
      ) : isError ? (
        <p className="py-8 text-center text-sm text-red-600">
          Notițele de întâlnire nu au putut fi încărcate.
        </p>
      ) : filtered.length === 0 && mode === 'list' ? (
        <p className="py-8 text-center text-gray-500">
          {notes.length === 0
            ? 'Nicio notiță de întâlnire. Adaugă una manual sau prin procesare AI.'
            : 'Nicio notiță nu corespunde filtrelor curente.'}
        </p>
      ) : (
        <ul className="mt-2 space-y-3">
          {filtered.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              open={openId === note.id}
              projectId={projectId}
              owner={owner}
              readOnly={readOnly}
              onToggle={() => setOpenId(openId === note.id ? null : note.id)}
              onDelete={async () => {
                if (await confirm({ message: 'Ștergi această notiță?', danger: true }))
                  deleteMutation.mutate(note.id);
              }}
              onReIngest={() => reIngestMutation.mutate(note.id)}
              reIngesting={reIngestMutation.isPending}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function actionItemCount(note: MeetingNote): number {
  if (!note.actionItems) return 0;
  return note.actionItems.split('\n').filter((l) => /^\s*[-*]\s+/.test(l) || /^\s*\d+\.\s+/.test(l))
    .length;
}

function NoteRow({
  note,
  open,
  owner,
  projectId,
  readOnly,
  onToggle,
  onDelete,
  onReIngest,
  reIngesting,
}: {
  note: MeetingNote;
  open: boolean;
  owner: MeetingNoteOwner;
  projectId?: string;
  readOnly?: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onReIngest: () => void;
  reIngesting: boolean;
}) {
  const aiCount = actionItemCount(note);
  const canReIngest =
    !readOnly && (note.ingestStatus === 'FAILED' || note.ingestStatus === 'PENDING');

  return (
    <li className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-3 px-4 py-3">
        <button type="button" onClick={onToggle} className="flex min-w-0 flex-1 text-left">
          <span className="mt-0.5 text-gray-400">
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
          <span className="ml-2 min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-gray-900">{note.title}</span>
            <span className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
              <span>{formatDate(note.date)}</span>
              <span className="rounded-full bg-brand-50 px-1.5 py-0.5 font-medium text-brand-700">
                {note.meetingType}
              </span>
              <IngestStatusBadge status={note.ingestStatus} />
              {note.attendees && <span className="truncate">· {note.attendees}</span>}
              {aiCount > 0 && (
                <span className="text-gray-400">
                  · {aiCount} {aiCount === 1 ? 'acțiune' : 'acțiuni'}
                </span>
              )}
              {note.s3Url && (
                <span className="inline-flex items-center gap-0.5 text-gray-400">
                  <FileText size={11} /> PDF
                </span>
              )}
            </span>
            {note.summary && !open && (
              <span className="mt-1 line-clamp-2 block text-sm text-gray-600">{note.summary}</span>
            )}
          </span>
        </button>
        <div className="flex items-center gap-1">
          {canReIngest && (
            <button
              type="button"
              onClick={onReIngest}
              disabled={reIngesting}
              title="Reprocesează"
              className="inline-flex items-center gap-1 rounded border border-gray-200 px-1.5 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw size={13} className={reIngesting ? 'animate-spin' : undefined} />
              Reprocesează
            </button>
          )}
          {!readOnly && (
            <button
              type="button"
              onClick={onDelete}
              className="text-gray-300 hover:text-red-500"
              aria-label="Șterge"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      {open && (
        <MeetingNoteDetail note={note} owner={owner} projectId={projectId} readOnly={readOnly} />
      )}
    </li>
  );
}
